<template>
  <article class="menu-option">
    <div class="left">
      <div class="left-header">
        <figure class="icon">
          <img src="@/assets/images/icon-topping.svg" alt="아이콘" />
        </figure>
        <h1 class="text">{{ t('title.option_select') }}</h1>
      </div>

      <div class="left-main">
        <figure class="menu-image">
          <MenuImage
            :menu-item="menu"
            :badge="menu.Badge"
            badge-size="large"
            :main-menu-badge-active="mainMenuBadgeActive"
            :ad-image-path="menu.ADImagePath"
          />
        </figure>
        <div class="menu-info">
          <h2 class="menu-name">{{ getNameByLocale(menu) }}</h2>
          <div class="menu-description" v-html="getNoteByLocale(menu, false, true)"></div>
        </div>
      </div>
      <div class="left-footer">
        <div class="counter">
          <BaseCounter v-model="qty" :disabled="false" :limit="limitCount" />
        </div>

        <div class="amount">
          <span class="amount-text">{{ t('label.total_amount') }}</span>
          <span class="amount-price">{{ formatCurrency(menuTotalAmount) }}</span>
        </div>
      </div>
    </div>
    <div class="right">
      <!-- 옵션 카테고리 -->
      <div class="right-header">
        <template v-if="toppingGroups && toppingGroups.length > 0">
          <MenuOptionSlider :groups="toppingGroups" />
        </template>
        <template v-else-if="setMenuGroups && setMenuGroups.length > 0">
          <MenuOptionSlider :groups="setMenuGroups" />
        </template>
      </div>

      <!-- 옵션 리스트 -->
      <div class="right-main cart-scroll-body">
        <MenuOptionGroupList
          v-cart-scroll-spy="{ offset: offset }"
          :menu="menu"
          :topping-groups="toppingGroups"
          :set-menu-groups="setMenuGroups"
          :column-count="2"
          @change="handleOptionChange"
        />
      </div>
      <div class="right-footer">
        <BaseButtonGroup>
          <BaseButton :flex="1" class="outline" @button-click="handleCloseClick">
            <template #icon>
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 25.075 25.075">
                <path
                  fill="#ffffff"
                  d="M24.258,100.207a2.509,2.509,0,1,0-3.548-3.548l-8.247,8.255L4.207,96.667a2.509,2.509,0,1,0-3.548,3.548l8.255,8.247L.667,116.718a2.509,2.509,0,1,0,3.548,3.548l8.247-8.255,8.255,8.247a2.509,2.509,0,1,0,3.548-3.548l-8.255-8.247Z"
                  transform="translate(0.075 -95.925)"
                />
              </svg>
            </template>
            {{ t('button.close') }}
          </BaseButton>
          <BaseButton v-if="!configStore.isMobileMenu" :flex="2" @button-click="handleAddCartClick">
            {{ t('button.cart_add') }}
          </BaseButton>
        </BaseButtonGroup>
      </div>
    </div>
  </article>
</template>

<script setup lang="ts">
  import BaseButton from '@/components/base/BaseButton.vue';
  import BaseButtonGroup from '@/components/base/BaseButtonGroup.vue';
  import BaseCounter from '@/components/base/BaseCounter.vue';
  import MenuImage from '@/components/MenuImage.vue';
  import MenuOptionGroupList from '@/components/MenuOptionGroupList.vue';
  import MenuOptionSlider from '@/components/MenuOptionSlider.vue';
  import { useMenu } from '@/composable/useMenu';
  import UserError from '@/error';
  import CartMenu from '@/interfaces/CartMenu';
  import CartMenuOption from '@/interfaces/CartMenuOption';
  import Menu from '@/interfaces/Menu';
  import SetMenuGroup from '@/interfaces/SetMenuGroup';
  import ToppingGroup from '@/interfaces/ToppingGroup';
  import { useCartStore } from '@/stores/cart';
  import { useConfigStore } from '@/stores/config';
  import { useMenuStore } from '@/stores/menu';
  import { formatCurrency, getNameByLocale, getNoteByLocale } from '@/utils';
  import { computed, onMounted, ref } from 'vue';
  import { useI18n } from 'vue-i18n';

  const props = defineProps<{
    menu: Menu;
    qty?: number;
  }>();
  const emits = defineEmits(['close']);

  const { t } = useI18n();
  const { isMainMenuBadgeActive } = useMenu();
  const configStore = useConfigStore();
  const menuStore = useMenuStore();
  const cartStore = useCartStore();

  // 토핑 그룹 리스트 조회
  const toppingGroups: ToppingGroup[] =
    props.menu.MenuType === 'M'
      ? menuStore.toppingGroups.filter((toppingGroup) => toppingGroup.TemplateCode === props.menu.ToppingTemplateCode)
      : [];

  // 세트메뉴 그룹 리스트 조회
  const setMenuGroups: SetMenuGroup[] =
    props.menu.MenuType === 'S'
      ? menuStore.setMenuGroups.filter((setGroup) => setGroup.ItemCode === props.menu.ItemCode)
      : [];

  const offset = ref();
  const qty = ref(props.qty || 1);
  const selectedTotalOptions = ref<CartMenuOption[]>([]);

  const mainMenuBadgeActive = computed(() => {
    return isMainMenuBadgeActive(props.menu);
  });

  // 메뉴 총 금액
  const menuTotalAmount = computed(() => {
    // 옵션 추가금액 계산
    let optionAddPrice = 0;
    optionAddPrice += selectedTotalOptions.value.reduce((acc, option) => {
      if (props.menu.MenuType === 'M') {
        // 토핑 추가금액
        const findTopping = menuStore.getTopping(option.GroupCode.toString(), option.OptionCode);
        return acc + menuStore.getToppingPrice(findTopping, props.menu.IsTax2) * option.Qty;
      } else if (props.menu.MenuType === 'S') {
        // 세트메뉴 추가금액
        const findSetMenu = menuStore.getSetMenu(option.MenuItemCode, Number(option.GroupCode), option.OptionCode);
        return acc + menuStore.getSetMenuPrice(findSetMenu, props.menu.IsTax2) * option.Qty;
      } else {
        return 0;
      }
    }, 0);
    // (메뉴금액 + 추가금액) * 수량 + 세금 (세금별도인 경우)
    return menuStore.getMenuAmount(
      props.menu.FinalPrice,
      optionAddPrice,
      qty.value,
      configStore.config.IsTaxIncluded,
      props.menu.IsTax,
      props.menu.IsTax2,
    );
  });

  // 한정수량
  const limitCount = computed(() => {
    const limit = menuStore.getLimitItem(props.menu.ItemCode)?.OrderLimitCnt;
    if (!limit) return undefined;

    const cartCount = cartStore.cart.CartMenus.find((cartMenu) => cartMenu.ItemCode === props.menu.ItemCode)?.Qty || 0;

    return limit - cartCount;
  });

  onMounted(() => {
    offset.value = document.querySelector<any>('.right-header')?.clientHeight + 20;
  });

  /**
   * 옵션을 변경할 때마다 업데이트
   * 선택한 옵션 리스트에서 변경된 그룹의 옵션을 모두 지운 후 업데이트된 토핑 리스트를 추가
   * @param groupCode 토핑 그룹코드
   * @param selectedOptions 선택된 옵션 리스트
   */
  const handleOptionChange = (groupCode: string | number, selectedOptions: CartMenuOption[]) => {
    const all = [...selectedTotalOptions.value];
    for (let i = 0; i < all.length; i++) {
      if (all[i].GroupCode === groupCode) {
        all.splice(i, 1);
        i--;
      }
    }
    selectedTotalOptions.value = all.concat(selectedOptions);
  };

  /** 닫기 버튼 클릭 */
  const handleCloseClick = () => emits('close');

  /** 장바구니 담기 버튼 클릭 */
  const handleAddCartClick = async () => {
    // 옵션 유효성 검사
    if (props.menu.MenuType === 'M' || props.menu.MenuType === 'S') {
      const invalidGroupNames: string[] = [];
      if (props.menu.MenuType === 'M') {
        // 토핑 메뉴인 경우 최소 수량 체크
        for (const toppingGroup of toppingGroups) {
          const count = selectedTotalOptions.value
            .filter((option) => option.GroupCode === toppingGroup.GroupCode)
            .reduce((accumulator, option) => accumulator + option.Qty, 0);

          if (count < toppingGroup.MinCount) {
            invalidGroupNames.push(getNameByLocale(toppingGroup));
          }
        }
      } else if (props.menu.MenuType === 'S') {
        // 세트 메뉴인 경우 필수 1개 선택 체크
        for (const setMenuGroup of setMenuGroups) {
          const count = selectedTotalOptions.value.filter(
            (option) => option.GroupCode === setMenuGroup.GroupCode,
          ).length;

          if (count < 1) {
            invalidGroupNames.push(getNameByLocale(setMenuGroup));
          }
        }
      }

      // 필수 옵션 검사
      if (invalidGroupNames.length > 0) {
        throw new UserError(`${t('error.required_option')} (${invalidGroupNames.join(', ')})`);
      }
    }

    // 장바구니 데이터 생성
    const menuData: CartMenu = {
      GroupCode: props.menu.GroupCode,
      ItemCode: props.menu.ItemCode,
      Qty: qty.value,
      Toppings: [],
      SetMenus: [],
    };

    // 같은 옵션을 선택했는지 구분하기 위해 옵션값 정렬 후 장바구니 데이터에 추가
    if (props.menu.MenuType === 'M') {
      selectedTotalOptions.value.sort((a, b) => {
        const aGroup = menuStore.getToppingGroup(a.GroupCode.toString());
        const aOption = menuStore.getTopping(a.GroupCode.toString(), a.OptionCode);
        const bGroup = menuStore.getToppingGroup(b.GroupCode.toString());
        const bOption = menuStore.getTopping(b.GroupCode.toString(), b.OptionCode);

        return (
          aGroup?.PositionZ - bGroup?.PositionZ ||
          aGroup?.PositionX - bGroup?.PositionX ||
          aOption.PositionZ - bOption.PositionZ ||
          aOption.PositionY - bOption.PositionY ||
          aOption.PositionX - bOption.PositionX
        );
      });
      menuData.Toppings = selectedTotalOptions.value;
    } else if (props.menu.MenuType === 'S') {
      selectedTotalOptions.value.sort((a, b) => {
        return Number(a.GroupCode) - Number(b.GroupCode) || Number(a.OptionCode) - Number(b.OptionCode);
      });
      menuData.SetMenus = selectedTotalOptions.value;
    }

    // 장바구니에 추가
    const isSuccess = await cartStore.addCartMenu(menuData);
    if (!isSuccess) return;

    // 옵션 선택 페이지 닫기
    emits('close');
  };
</script>

<style scoped lang="scss">
  @use '@/assets/scss/variables' as vars;

  .menu-option {
    display: grid;
    grid-template-columns: 27.0896vw 1fr;
    width: 100%;
    height: 100vh;
    @media (max-width: vars.$sm) {
      grid-template-columns: none;
      grid-template-rows: auto 1fr;
    }

    // 헤더 공통 스타일
    .left-header,
    .right-header {
      display: grid;
      align-items: center;
      height: 6.25vw;
      margin-top: 0.7813vw;
      border-bottom: 0.0781vw solid var(--border-color-secondary);

      @media (max-width: vars.$sm) {
        height: auto;
        border-bottom: none;
      }
    }

    .left {
      display: grid;
      grid-template-rows: auto 1fr auto;
      gap: 1.5625vw;
      min-height: 0;
      padding: 0 0 1.0448vw 2.8358vw;
      background: #f3efef;
      @media (max-width: vars.$sm) {
        gap: 5px;
        padding: 10px;
      }

      .left-header,
      .left-main,
      .left-footer {
        padding-right: 2.0896vw;
      }

      .left-header {
        grid-template-columns: auto 1fr;
        gap: 0.7463vw;

        .icon {
          display: flex;
          justify-content: center;
          img {
            width: 2.6866vw;
            @media (max-width: vars.$sm) {
              width: 22px;
            }
          }
        }

        .text {
          min-width: 0;
          overflow: hidden; /* 넘친 부분 숨기기 */
          white-space: nowrap; /* 줄바꿈 금지 */
          text-overflow: ellipsis; /* 말줄임(...) 표시 */
          font-size: 2.6563vw;
          @media (max-width: vars.$sm) {
            font-size: 22px;
          }
        }
      }

      .left-main {
        display: grid;
        grid-template-rows: auto 1fr;
        gap: 0.7813vw;
        height: 100%;
        overflow: hidden;
        @media (max-width: vars.$sm) {
          grid-template-columns: 150px 1fr;
          grid-template-rows: none;
          gap: 10px;
          margin: 10px 0;
        }

        .menu-info {
          display: flex;
          flex-direction: column;
          gap: 0.3906vw;
          min-height: 0;
          overflow-y: hidden;

          .menu-name {
            text-align: center;
            font-size: 1.8657vw;
            font-weight: 500;
            margin-bottom: 0.2985vw;
            word-break: break-all;
            @media (max-width: vars.$sm) {
              text-align: left;
              font-size: 16px;
            }
          }

          .menu-description {
            min-height: 0;
            height: 100%;
            font-size: 1.194vw;
            color: var(--text-secondary);
            text-align: left;
            word-break: break-all;
            white-space: pre-wrap;
            overflow-y: auto;
            @media (max-width: vars.$sm) {
              font-size: 12px;
              line-height: 16px;
              height: 100px;
            }
          }
        }
      }

      .left-footer {
        display: flex;
        flex-direction: column;
        gap: 1.1719vw;
        align-items: center;
        @media (max-width: vars.$sm) {
          flex-direction: row;
          justify-content: space-between;
        }

        .amount {
          .amount-text {
            font-size: 1.8657vw;
            font-weight: 700;
            margin-right: 1.0448vw;
            @media (max-width: vars.$sm) {
              font-size: 12px;
            }
          }

          .amount-price {
            font-size: 2.3881vw;
            font-weight: 700;
            color: var(--point-color-secondary);
            @media (max-width: vars.$sm) {
              font-size: 16px;
            }
          }
        }
      }
    }

    .right {
      display: grid;
      grid-template-rows: auto 1fr auto;
      min-height: 0;
      padding: 0 2.8358vw 1.0448vw 0;
      @media (max-width: vars.$sm) {
        padding: 10px;
        gap: 10px;
      }

      .right-header,
      .right-main,
      .right-footer {
        padding-left: 1.3433vw;
      }

      .right-main {
        margin-top: 1.5625vw;
        margin-bottom: 1.5625vw;
        min-height: 0;
        overflow-y: auto;
        @media (max-width: vars.$sm) {
        }
      }
    }
  }
</style>
